// File: frontend/src/pages/EntityProfilePage.tsx
// Entity profile: everything the corpus knows about a canonical entity
// (threat actor, malware, tool, campaign) in one page — identity & aliases,
// activity timeline, and connected entities from the Knowledge Graph.
import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';
import {
    Box,
    Container,
    Paper,
    Typography,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    Button,
    Link,
    Tooltip,
    alpha,
    Fade,
} from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BugReportIcon from '@mui/icons-material/BugReport';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import DownloadIcon from '@mui/icons-material/Download';
import GridOnIcon from '@mui/icons-material/GridOn';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useApi } from '../hooks/useApi';
import { cyber } from '../theme';
import { ExploitationBadgeStack } from '../components/ExploitationBadges';
import MitreHeatmap from '../components/MitreHeatmap';
import { HighlightedTechnique } from '../types/mitreHeatmap';

export const ENTITY_TYPE_COLORS: { [key: string]: string } = {
    ThreatActor: '#ff5c5c',
    Malware: '#ff9f43',
    Tool: '#54a0ff',
    AttackPattern: '#a55eea',
    Campaign: '#f368e0',
    Indicator: '#10ac84',
    Vulnerability: '#ee5253',
    Identity: '#9fa4c0',
};

interface TimelineAppearance {
    report_id: string;
    title: string | null;
    date: string | null;
    source: string | null;
    surface_form: string | null;
}

interface ProfileRelatedEntity {
    id: string;
    name: string;
    type: string;
    weight: number;
    rel_types: string[];
}

interface EntityProfile {
    canon_id: string;
    name: string;
    type: string;
    aliases: string[] | null;
    external_ids: string[] | null;
    total_reports: number;
    total_mentions: number;
    first_seen: string | null;
    last_seen: string | null;
    appearances: TimelineAppearance[];
    related: { [entityClass: string]: (ProfileRelatedEntity & { external_ids?: string[] | null })[] };
}

interface EntityCveItem {
    cve_id: string;
    description: string | null;
    cvss_v3_score: number | null;
    cvss_v3_severity: string | null;
    total_mentions: number;
    exploited_in_wild: boolean;
    last_seen_date: string | null;
    epss_score: number | null;
    epss_percentile: number | null;
    in_cisa_kev: boolean;
    kev_date_added: string | null;
    kev_due_date: string | null;
    kev_ransomware_use: boolean;
    risk_score: number | null;
}

interface EntityCvesResponse {
    total: number;
    cves: EntityCveItem[];
}

interface EntityIocItem {
    indicator: string;
    indicator_type: string | null;
    confidence: string | null;
    threat_actor: string | null;
    malware_family: string | null;
    kill_chain_phase: string | null;
    article_count: number;
    first_seen: string | null;
    last_seen: string | null;
}

interface EntityIocsResponse {
    total: number;
    iocs: EntityIocItem[];
}

const RELATED_CLASS_ORDER = ['ThreatActor', 'Malware', 'Tool', 'AttackPattern', 'Campaign', 'Vulnerability', 'Identity', 'Indicator'];
const RELATED_SHOWN_PER_CLASS = 15;

/** ATT&CK external ids (G0032, S0002, T1059...) link to the MITRE site. */
const attackUrl = (eid: string): string | null => {
    const m = eid.match(/^([GST])(\d{4})(?:\.(\d{3}))?$/);
    if (!m) return null;
    const base: { [k: string]: string } = { G: 'groups', S: 'software', T: 'techniques' };
    const path = m[3] ? `${m[1]}${m[2]}/${m[3]}` : `${m[1]}${m[2]}`;
    return `https://attack.mitre.org/${base[m[1]]}/${path}/`;
};

/** Compact monthly activity bars built from the appearance dates. */
const ActivitySparkline: React.FC<{ appearances: TimelineAppearance[] }> = ({ appearances }) => {
    const buckets = React.useMemo(() => {
        const byMonth: { [month: string]: number } = {};
        appearances.forEach((a) => {
            if (a.date) {
                const month = a.date.slice(0, 7); // YYYY-MM
                byMonth[month] = (byMonth[month] || 0) + 1;
            }
        });
        const months = Object.keys(byMonth).sort();
        if (months.length === 0) return [];
        // Fill gaps between first and last month
        const filled: { month: string; count: number }[] = [];
        const [startY, startM] = months[0].split('-').map(Number);
        const [endY, endM] = months[months.length - 1].split('-').map(Number);
        for (let y = startY, m = startM; y < endY || (y === endY && m <= endM); m === 12 ? (y++, m = 1) : m++) {
            const key = `${y}-${String(m).padStart(2, '0')}`;
            filled.push({ month: key, count: byMonth[key] || 0 });
        }
        return filled.slice(-24); // last 24 months max
    }, [appearances]);

    if (buckets.length < 2) return null;
    const max = Math.max(...buckets.map((b) => b.count), 1);

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Mentions per month
            </Typography>
            <Stack direction="row" spacing={0.4} alignItems="flex-end" sx={{ height: 48 }}>
                {buckets.map((b) => (
                    <Tooltip key={b.month} title={`${b.month}: ${b.count} mention${b.count === 1 ? '' : 's'}`}>
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 6,
                                height: `${Math.max((b.count / max) * 100, b.count > 0 ? 12 : 4)}%`,
                                bgcolor: b.count > 0 ? alpha(cyber.accent.cyan, 0.7) : alpha('#9fa4c0', 0.2),
                                borderRadius: 0.5,
                            }}
                        />
                    </Tooltip>
                ))}
            </Stack>
        </Box>
    );
};

const StatCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
    <Paper sx={{ p: 1.5, px: 2.5, textAlign: 'center', bgcolor: alpha(cyber.primary, 0.05), border: `1px solid ${alpha(cyber.primary, 0.15)}` }}>
        <Typography variant="h6" sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan, fontWeight: 700 }}>
            {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
    </Paper>
);

const EntityProfilePage: React.FC = () => {
    const { canonId } = useParams<{ canonId: string }>();
    const { authenticatedFetcher } = useApi();

    const { data: profile, error, isLoading } = useSWR<EntityProfile>(
        canonId ? `/api/kg/entity/${canonId}/profile` : null,
        authenticatedFetcher,
        { revalidateOnFocus: false }
    );

    // Enrichment sections load independently; failures stay silent
    const { data: cvesData } = useSWR<EntityCvesResponse>(
        canonId ? `/api/kg/entity/${canonId}/cves` : null,
        authenticatedFetcher,
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );
    const { data: iocsData } = useSWR<EntityIocsResponse>(
        canonId ? `/api/kg/entity/${canonId}/iocs` : null,
        authenticatedFetcher,
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    // ATT&CK techniques from connected AttackPattern entities (T-ids only)
    const attackHighlights: HighlightedTechnique[] = React.useMemo(() => {
        const patterns = profile?.related?.AttackPattern || [];
        const seen = new Set<string>();
        const highlights: HighlightedTechnique[] = [];
        patterns.forEach((p) => {
            (p.external_ids || []).forEach((eid) => {
                if (/^T\d{4}(\.\d{3})?$/.test(eid) && !seen.has(eid)) {
                    seen.add(eid);
                    highlights.push({ techniqueId: eid, tactic: '', comment: p.name });
                }
            });
        });
        return highlights;
    }, [profile]);

    const handleIocCsvDownload = () => {
        if (!iocsData?.iocs?.length || !profile) return;
        const esc = (v: string | null | undefined) => `"${(v || '').replace(/"/g, '""')}"`;
        const header = 'indicator,type,confidence,threat_actor,malware_family,kill_chain_phase,article_count,first_seen,last_seen\n';
        const rows = iocsData.iocs.map((i) =>
            [esc(i.indicator), esc(i.indicator_type), esc(i.confidence), esc(i.threat_actor), esc(i.malware_family), esc(i.kill_chain_phase), i.article_count, esc(i.first_seen), esc(i.last_seen)].join(',')
        );
        const blob = new Blob([header + rows.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `iocs-${profile.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}><CircularProgress /></Box>;
    }
    if (error || !profile) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Button component={RouterLink} to="/entities" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                    Threat Entities
                </Button>
                <Alert severity={error?.message?.includes('404') ? 'warning' : 'error'} sx={{ borderRadius: 2 }}>
                    {error?.message?.includes('404')
                        ? 'Entity not found in the Knowledge Graph.'
                        : 'Could not load the entity profile. The Knowledge Graph may be unavailable.'}
                </Alert>
            </Container>
        );
    }

    const typeColor = ENTITY_TYPE_COLORS[profile.type] || '#9fa4c0';
    const recentAppearances = [...profile.appearances].reverse();

    // Case-insensitive alias dedupe ("DragonFly" vs "Dragonfly"), name excluded
    const displayAliases: string[] = [];
    const seenAliases = new Set<string>([profile.name.toLowerCase()]);
    (profile.aliases || []).forEach((a) => {
        const key = a.toLowerCase();
        if (!seenAliases.has(key)) {
            seenAliases.add(key);
            displayAliases.push(a);
        }
    });

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Button component={RouterLink} to="/entities" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
                Threat Entities
            </Button>

            {/* Header */}
            <Fade in timeout={400}>
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha(typeColor, 0.35)}`, background: `linear-gradient(135deg, ${alpha(typeColor, 0.08)} 0%, transparent 60%)` }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                        <Box>
                            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="h4" fontWeight="bold">{profile.name}</Typography>
                                <Chip label={profile.type} sx={{ bgcolor: alpha(typeColor, 0.15), color: typeColor, fontWeight: 'bold', border: `1px solid ${alpha(typeColor, 0.4)}` }} />
                            </Stack>

                            {profile.external_ids && profile.external_ids.length > 0 && (
                                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                    {profile.external_ids.map((eid) => {
                                        const url = attackUrl(eid);
                                        const chip = (
                                            <Chip
                                                key={eid}
                                                label={eid}
                                                size="small"
                                                clickable={!!url}
                                                icon={url ? <OpenInNewIcon sx={{ fontSize: 12 }} /> : undefined}
                                                sx={{ fontFamily: cyber.fontMono, fontSize: '0.7rem', height: 22 }}
                                            />
                                        );
                                        return url ? (
                                            <Link key={eid} href={url} target="_blank" rel="noopener noreferrer" underline="none">{chip}</Link>
                                        ) : chip;
                                    })}
                                </Stack>
                            )}

                            {displayAliases.length > 0 && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">Also known as</Typography>
                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                                        {displayAliases.slice(0, 12).map((alias) => (
                                            <Chip key={alias} label={alias} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>

                        <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                            <StatCard label="Reports" value={profile.total_reports} />
                            <StatCard label="First seen" value={profile.first_seen ? profile.first_seen.slice(0, 10) : '—'} />
                            <StatCard label="Last seen" value={profile.last_seen ? profile.last_seen.slice(0, 10) : '—'} />
                        </Stack>
                    </Stack>

                    <Box sx={{ mt: 2.5 }}>
                        <ActivitySparkline appearances={profile.appearances} />
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button
                            component={RouterLink}
                            to="/knowledge-graph"
                            size="small"
                            startIcon={<HubIcon />}
                            sx={{ textTransform: 'none' }}
                        >
                            Explore in STIX Constellation
                        </Button>
                    </Stack>
                </Paper>
            </Fade>

            {/* Exploited CVEs (via alias match against the CVE corpus) */}
            {cvesData && cvesData.total > 0 && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha('#d32f2f', 0.25)}` }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <BugReportIcon sx={{ color: '#d32f2f', fontSize: 24 }} />
                        <Typography variant="h6" fontWeight="bold">Exploited CVEs ({cvesData.total})</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        CVEs attributed to this entity across the corpus, highest risk first.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>CVE</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Severity</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Exploitation</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Risk</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Reports</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cvesData.cves.map((cve) => (
                                    <TableRow key={cve.cve_id} hover>
                                        <TableCell>
                                            <Link
                                                href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontFamily: cyber.fontMono, fontSize: '0.8rem', fontWeight: 'bold', color: alpha(cyber.accent.cyan, 0.9) }}
                                            >
                                                {cve.cve_id}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            {cve.cvss_v3_severity ? (
                                                <Chip
                                                    label={cve.cvss_v3_score ? `${cve.cvss_v3_severity} (${cve.cvss_v3_score})` : cve.cvss_v3_severity}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                                                />
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <ExploitationBadgeStack signals={cve} />
                                        </TableCell>
                                        <TableCell align="right">
                                            {cve.risk_score !== null && (
                                                <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, fontWeight: 'bold', color: cve.risk_score >= 85 ? '#d32f2f' : cyber.accent.amber }}>
                                                    {cve.risk_score.toFixed(1)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan }}>
                                                {cve.total_mentions}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Related entities by class */}
            {RELATED_CLASS_ORDER.filter((cls) => profile.related[cls]?.length).length > 0 && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha(cyber.primary, 0.2)}` }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>Connected Entities</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Observed relationships from the Knowledge Graph, strongest first.
                    </Typography>
                    <Stack spacing={2}>
                        {RELATED_CLASS_ORDER.filter((cls) => profile.related[cls]?.length).map((cls) => {
                            const color = ENTITY_TYPE_COLORS[cls] || '#9fa4c0';
                            const items = profile.related[cls];
                            return (
                                <Box key={cls}>
                                    <Typography variant="subtitle2" sx={{ color, mb: 0.5 }}>
                                        {cls} ({items.length})
                                    </Typography>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                        {items.slice(0, RELATED_SHOWN_PER_CLASS).map((e) => (
                                            <Tooltip key={e.id} title={`${e.weight} relationship${e.weight === 1 ? '' : 's'}${e.rel_types.length ? ` — ${e.rel_types.join(', ')}` : ''}`}>
                                                <Chip
                                                    component={RouterLink}
                                                    to={`/entity/${e.id}`}
                                                    clickable
                                                    label={e.name.length > 40 ? `${e.name.slice(0, 40)}…` : e.name}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '0.72rem',
                                                        color,
                                                        bgcolor: alpha(color, 0.1),
                                                        border: `1px solid ${alpha(color, 0.3)}`,
                                                        '&:hover': { bgcolor: alpha(color, 0.22) },
                                                    }}
                                                />
                                            </Tooltip>
                                        ))}
                                        {items.length > RELATED_SHOWN_PER_CLASS && (
                                            <Chip label={`+${items.length - RELATED_SHOWN_PER_CLASS} more`} size="small" sx={{ fontSize: '0.72rem' }} />
                                        )}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Paper>
            )}

            {/* MITRE ATT&CK heatmap from connected AttackPattern techniques */}
            {attackHighlights.length > 0 && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha('#a55eea', 0.3)}` }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <GridOnIcon sx={{ color: '#a55eea', fontSize: 24 }} />
                        <Typography variant="h6" fontWeight="bold">ATT&CK Techniques ({attackHighlights.length})</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Techniques connected to this entity in the Knowledge Graph. Coverage depends on
                        ATT&CK id extraction quality and will improve as new reports are normalized.
                    </Typography>
                    <MitreHeatmap directHighlights={attackHighlights} />
                </Paper>
            )}

            {/* Associated IOCs */}
            {iocsData && iocsData.total > 0 && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, border: `1px solid ${alpha('#10ac84', 0.3)}` }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FingerprintIcon sx={{ color: '#10ac84', fontSize: 24 }} />
                            <Typography variant="h6" fontWeight="bold">Associated IOCs ({iocsData.total})</Typography>
                        </Stack>
                        <Button size="small" onClick={handleIocCsvDownload} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
                            Download CSV
                        </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Indicators attributed to this entity, most recent first.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Indicator</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Kill Chain</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Reports</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Last Seen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {iocsData.iocs.slice(0, 100).map((ioc, idx) => (
                                    <TableRow key={`${ioc.indicator}-${idx}`} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, fontSize: '0.75rem', wordBreak: 'break-all' }}>
                                                {ioc.indicator}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={(ioc.indicator_type || 'unknown').split(':')[0]} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">{ioc.kill_chain_phase || '—'}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan }}>{ioc.article_count}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption" sx={{ fontFamily: cyber.fontMono, color: 'text.secondary' }}>
                                                {ioc.last_seen ? ioc.last_seen.slice(0, 10) : '—'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {iocsData.total > 100 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Showing 100 of {iocsData.total} — use Download CSV for the full list.
                        </Typography>
                    )}
                </Paper>
            )}

            {/* Report timeline */}
            <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${alpha(cyber.primary, 0.2)}` }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Report Timeline ({profile.total_reports})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Every report mentioning this entity, most recent first.
                </Typography>
                <Stack spacing={1}>
                    {recentAppearances.map((a, idx) => (
                        <Paper
                            key={`${a.report_id}-${idx}`}
                            component={RouterLink}
                            to={`/report/${a.report_id}`}
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                textDecoration: 'none',
                                bgcolor: alpha(cyber.primary, 0.04),
                                border: `1px solid ${alpha(cyber.primary, 0.12)}`,
                                transition: 'all 0.15s ease-in-out',
                                '&:hover': { bgcolor: alpha(cyber.primary, 0.09), transform: 'translateX(4px)' },
                            }}
                        >
                            <ArticleIcon sx={{ color: alpha(cyber.accent.cyan, 0.7), fontSize: 20, flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" fontWeight="bold" color="text.primary" noWrap>
                                    {a.title || a.report_id}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {a.source && (
                                        <Typography variant="caption" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono }}>
                                            {a.source}
                                        </Typography>
                                    )}
                                    {a.surface_form && a.surface_form !== profile.name && (
                                        <Typography variant="caption" color="text.secondary">
                                            mentioned as "{a.surface_form}"
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: cyber.fontMono, flexShrink: 0 }}>
                                {a.date ? a.date.slice(0, 10) : ''}
                            </Typography>
                        </Paper>
                    ))}
                </Stack>
            </Paper>
        </Container>
    );
};

export default EntityProfilePage;
